<template>
  <section class="home-intro-slides">
    <h2 class="welcome-title">{{ $t("homePage.introSlides.welcome") }}</h2>
    <button class="close" type="button" :aria-label="$t('homePage.introSlides.close')" @click="onCloseClick">
      <IconClose />
    </button>

    <div class="swiper-area">
      <swiper ref="introSwiper" class="swiper" :options="swiperOptions" @slide-change="onSlideChange">
        <swiper-slide v-for="(slide, i) in slides" :key="i" class="slide">
          <div class="slide-image-wrapper">
            <img class="slide-image" :src="slide.imageSrc" :alt="$t(slide.altKey)" draggable="false" />
          </div>
          <div class="slide-text">
            <h3 class="slide-title">{{ $t(slide.titleKey) }}</h3>
            <p class="slide-body">{{ $t(slide.bodyKey) }}</p>
          </div>
        </swiper-slide>
      </swiper>
    </div>

    <ol class="dots" role="tablist">
      <li
        v-for="(_, i) in slides"
        :key="i"
        class="dot"
        :class="{ active: i === currentIndex }"
        :aria-selected="i === currentIndex"
        role="tab"
        @click="onDotClick(i)"
      />
    </ol>
  </section>
</template>

<script lang="ts">
import Vue from "vue";
import { Swiper, SwiperSlide } from "vue-awesome-swiper";
import "swiper/css/swiper.css";
import IconClose from "~icons/figma/Close";

const introSlide1 = require("@/assets/img/onboarding/intro-slide-1.webp");
const introSlide2 = require("@/assets/img/onboarding/intro-slide-2.webp");
const introSlide3 = require("@/assets/img/onboarding/intro-slide-3.webp");

interface Slide {
  imageSrc: string;
  titleKey: string;
  bodyKey: string;
  altKey: string;
}

interface SwiperInstance {
  realIndex: number;
  slideToLoop: (index: number) => void;
}

interface Data {
  currentIndex: number;
  swiperOptions: Record<string, unknown>;
}

interface Methods {
  getSwiper(): SwiperInstance | undefined;
  onSlideChange(): void;
  onDotClick(index: number): void;
  onCloseClick(): void;
}

interface Computed {
  slides: Slide[];
}

export default Vue.extend<Data, Methods, Computed, {}>({
  components: { Swiper, SwiperSlide, IconClose },
  data() {
    return {
      currentIndex: 0,
      swiperOptions: {
        loop: true,
        grabCursor: true,
        slidesPerView: 1,
        speed: 300,
      },
    };
  },
  computed: {
    slides() {
      return [
        {
          imageSrc: introSlide1,
          titleKey: "homePage.introSlides.slide1.title",
          bodyKey: "homePage.introSlides.slide1.body",
          altKey: "homePage.introSlides.slide1.alt",
        },
        {
          imageSrc: introSlide2,
          titleKey: "homePage.introSlides.slide2.title",
          bodyKey: "homePage.introSlides.slide2.body",
          altKey: "homePage.introSlides.slide2.alt",
        },
        {
          imageSrc: introSlide3,
          titleKey: "homePage.introSlides.slide3.title",
          bodyKey: "homePage.introSlides.slide3.body",
          altKey: "homePage.introSlides.slide3.alt",
        },
      ];
    },
  },
  methods: {
    getSwiper() {
      return (this.$refs.introSwiper as { $swiper?: SwiperInstance } | undefined)?.$swiper;
    },
    onSlideChange() {
      const swiper = this.getSwiper();
      if (swiper) {
        this.currentIndex = swiper.realIndex;
      }
    },
    onDotClick(index: number) {
      const swiper = this.getSwiper();
      if (swiper) {
        swiper.slideToLoop(index);
      }
    },
    onCloseClick() {
      this.$emit("dismissed");
    },
  },
});
</script>

<style lang="scss" scoped>
.home-intro-slides {
  position: relative;
  background-color: var(--color-surface-base);
  border-radius: var(--radius-md);
  padding: 16px 0px;
  box-sizing: border-box;
  overflow: hidden;
}

.welcome-title {
  margin: 0 0 8px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  text-align: center;
}

.close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text-low);
  border-radius: var(--radius-sm);
  cursor: pointer;
  flex-shrink: 0;
  font-size: var(--font-size-xs);
  line-height: 1;
  z-index: 1;
}

.swiper-area {
  height: 380px;
  width: 100%;
  min-width: 0;
  position: relative;
}

.slide {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  padding: 8px 20px;
  box-sizing: border-box;
}

.slide-image-wrapper {
  width: 100%;
  flex: 0 0 auto;
  height: 150px;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slide-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
}

.slide-text {
  width: 100%;
  text-align: center;
}

.slide-title {
  margin: 0 0 8px;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.slide-body {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
  line-height: var(--line-height-default);
  white-space: pre-line;
  text-align: left;
}

.dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
}

.dot {
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  border-radius: var(--radius-rounded);
  background-color: var(--color-inactive);
  cursor: pointer;
  transition: background-color 0.2s ease;
  box-sizing: border-box;

  &.active {
    background-color: var(--color-primary);
  }
}

@include mq(pc) {
  .home-intro-slides {
    padding: 12px 0px 4px;
  }

  .close {
    top: 20px;
    right: 20px;
  }

  .welcome-title {
    margin: 0 0 12px;
  }

  .swiper-area {
    height: 200px;
  }

  .slide {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 32px;
    padding: 0 24px;
  }

  .slide-image-wrapper {
    width: 45%;
    flex: 0 0 45%;
    max-width: 320px;
    height: auto;
    aspect-ratio: 662 / 372;
    background-color: var(--color-surface-layer);
    border-radius: var(--radius-sm);
    padding: 16px;
    box-sizing: border-box;
  }

  .slide-text {
    flex: 1 1 auto;
    width: auto;
    text-align: left;
  }

  .slide-title {
    font-size: var(--font-size-md);
    margin-bottom: 12px;
  }

  .slide-body {
    font-size: var(--font-size-sm);
  }
}
</style>

<style lang="scss">
/* Swiper の内部要素 (.swiper-container / .swiper-wrapper / .swiper-slide) は
   render 関数で生成され、scoped CSS の data-v が伝播しないことがあるため
   global で明示する */
.home-intro-slides .swiper-area {
  .swiper-container {
    height: 100%;
    width: 100%;
    overflow: visible;
  }

  .swiper-wrapper {
    height: 100%;
  }

  .swiper-slide {
    height: 100%;
    width: 100%;
  }
}
</style>
